<template>
  <div class="dashboard" :class="{ 'dark-mode': isDarkMode }">
    <!-- Sidebar -->
    <Sidebar 
      :is-expanded="isExpanded" 
      :is-dark-mode="isDarkMode" 
      :user="user"
      @toggle-theme="toggleTheme"
      @logout="logout"
    />

    <!-- Main Content -->
    <main class="main-content" :class="{ 'expanded': isExpanded }">
      
      <router-view></router-view>
    </main>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import Sidebar from './Sidebar.vue';
import { apiBaseUrl } from '@/config';

export default {
  name: 'Dashboard',
  components: {
    Sidebar
  },
  data() {
    return {
      isDarkMode: false,
      isExpanded: false,
      user: null,
      stats: {
        chatbotCount: 0,
        userCount: 0,
        discussionCount: 0,
        fourLastDiscussions:[]
      },
      chatbots: [
        {
          id: 1,
          name: 'Assistant Client',
          description: 'Chatbot pour le support client',
          conversations: 1234,
          users: 567,
          uptime: 99.9
        },
        {
          id: 2,
          name: 'FAQ Bot',
          description: 'Réponses aux questions fréquentes',
          conversations: 890,
          users: 234,
          uptime: 99.5
        }
      ],
      selectedProject: 'iOS App Project'
    }
  },
  computed: {
    totalConversations() {
      return this.stats.discussionCount;
    },
    activeBots() {
      return this.stats.chatbotCount;
    }
  },
  watch: {
    isDarkMode: {
      handler(newValue) {
        localStorage.setItem('darkMode', newValue);
        document.documentElement.classList.toggle('dark', newValue);
      }
    }
  },
  async mounted() {
    this.checkUser();
    this.isDarkMode = localStorage.getItem('darkMode') === 'true';
    
    // Add event listeners for sidebar hover
    const sidebar = document.querySelector('.sidebar');
    sidebar.addEventListener('mouseenter', () => this.isExpanded = true);
    sidebar.addEventListener('mouseleave', () => this.isExpanded = false);

    // Fetch stats
    await this.fetchStats();
  },
  methods: {
    async fetchStats() {
      try {
        // Récupérer le token depuis localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          console.error('Aucune donnée utilisateur trouvée');
          return;
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        if (!token) {
          console.error('Token non trouvé dans les données utilisateur');
          return;
        }
        
        // Faire la requête avec le token d'authentification
        const response = await fetch(`${apiBaseUrl}/get/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.message === "Statistiques récupérées avec succès.") {
          this.stats = data.stats;
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      }
    },
    formatNumber(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    checkUser() {
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.$router.push('/login');
        return;
      }
      try {
        const parsedUser = JSON.parse(userData);
        this.setUser(parsedUser.data);
        
      } catch (error) {
        console.error('Error parsing user data:', error);
        this.clearUser();
      }
    },
    setUser(userData) {
      this.user = userData;
      localStorage.setItem('user', JSON.stringify({ data: userData }));
    },
    clearUser() {
      this.user = null;
      localStorage.removeItem('user');
      this.$router.push('/login');
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      document.body.classList.toggle('dark-mode');
    },
    logout() {
      this.clearUser();
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showNotificationMessage('Copié dans le presse-papiers !', 'copy');
      }).catch(err => {
        console.error('Erreur lors de la copie:', err);
        this.showNotificationMessage('Erreur lors de la copie', 'error');
      });
    },
    showNotificationMessage(message, type = 'success') {
      this.notificationMessage = message;
      this.notificationType = type;
      this.showNotification = true;
    }
  },
  provide() {
    return {
      stats: this.stats,
      chatbots: this.chatbots
    }
  }
}
</script>

<style scoped>
:root {
  /* Light Mode Colors */
  --bg-primary: #F7F7F7;
  --bg-secondary: #FFFFFF;
  --bg-tertiary: #F0F2F5;
  --text-primary: #111B21;
  --text-secondary: #667781;
  --text-tertiary: #8696A0;
  --border-color: #E9EDEF;
  --accent-green: #1ABB34;
  --accent-light-green: #E8F8EA;
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Dark Mode Colors */
.dashboard.dark-mode {
  --bg-primary: #111B21;
  --bg-secondary: #1F2937;
  --bg-tertiary: #374151;
  --text-primary: #FFFFFF;
  --text-secondary: #9CA3AF;
  --text-tertiary: #6B7280;
  --border-color: #374151;
}

.dashboard.dark-mode .metric-card,
.dashboard.dark-mode .stat-item,
.dashboard.dark-mode .project-selector,
.dashboard.dark-mode .right-sidebar {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

.dashboard.dark-mode h1,
.dashboard.dark-mode .metric-value,
.dashboard.dark-mode .stat-item span,
.dashboard.dark-mode .stat-item .value {
  color: var(--text-primary);
}

.dashboard.dark-mode .header-subtitle {
  color: var(--text-secondary);
}

/* Theme Switch Styles */
.header-actions-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme-switch {
  display: flex;
  align-items: center;
}

.theme-btn {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.theme-btn:hover {
  background: var(--bg-tertiary);
  color: var(--accent-green);
}

/* Layout */
.dashboard {
  display: flex;
  min-height: 100vh;
  background: #F5F7FA;
  position: relative;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 240px;
  transition: margin-left 0.3s ease;
  min-height: 100vh;
  background: var(--bg-color);
  position: relative;
  overflow-x: hidden;
}

.main-content.expanded {
  margin-left: 240px;
}

/* Right Sidebar */
.right-sidebar {
  background: var(--bg-secondary);
  padding: 24px;
  border-left: 1px solid var(--border-color);
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  overflow-y: auto;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.date-nav {
  display: flex;
  align-items: center;
  gap: 16px;
}

.nav-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: none;
  border-radius: 8px;
  color: var(--text-primary);
  cursor: pointer;
}

.more-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
}

@media (max-width: 1400px) {
  .main-content {
    width: calc(100% - 400px);
  }
}

@media (max-width: 1200px) {
  .main-content {
    width: calc(100% - 80px);
    right: 0;
  }

  .sidebar.expanded + .main-content {
    margin-left: 240px;
  }

  .right-sidebar {
    display: none;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .main-content.expanded {
    margin-left: 0;
  }
}
</style> 